import Vue from 'vue'
import { computePath } from '@/collections/onboarding'
import { auth as authApi, account as accountApi } from '@/backend/api'

const getDefaultResponses = () => ({
  role: '',
  channels: [],
  charge_type: '',
  customer_type: '',
  active_clients: '',
  pricing_model: '',
  billing_frequency: '',
  invoicing_method: '',
  payment_method: '',
  payment_processor: ''
})

const state = {
  responses: getDefaultResponses(),
  path: '',
  completed: false,
  orgCreated: false,
  vistaOrquestadaSeen: false,
  onboardingId: null,
  profileLoaded: false,
  stateLoaded: false
}

const ORG_FIELDS = [
  'charge_type', 'customer_type', 'active_clients', 'pricing_model',
  'billing_frequency', 'invoicing_method', 'payment_method',
  'payment_processor'
]

const actions = {
  LOAD_USER_PROFILE: ({ commit, state }) => {
    if (state.profileLoaded) return Promise.resolve()
    return authApi.getUser()
      .then(response => {
        const { role, channels } = response.data
        if (role) commit('SET_RESPONSE', { key: 'role', value: role })
        if (channels && channels.length) commit('SET_RESPONSE', { key: 'channels', value: channels })
        commit('SET_PROFILE_LOADED', true)
      })
  },

  SAVE_USER_PROFILE: ({ state }) => {
    return authApi.patchUser({
      role: state.responses.role,
      channels: state.responses.channels
    })
  },

  LOAD_STATE: ({ commit, rootState }) => {
    // The /onboardings/ endpoint requires the x-simple-workspace header, which is only
    // injected when auth.account.id is set. A brand-new user without an organization
    // has no account.id yet, so skip the request entirely to avoid a 400 and let the
    // create_org step run without upstream rejections poisoning the Promise chain.
    if (!rootState.auth.account || !rootState.auth.account.id) {
      return Promise.resolve()
    }
    return accountApi.list('onboardings')
      .then(response => {
        const results = response.data.results
        if (results && results.length) {
          const ws = results[0]
          commit('SET_ONBOARDING_ID', ws.id)
          ORG_FIELDS.forEach(key => {
            if (ws[key]) commit('SET_RESPONSE', { key, value: ws[key] })
          })
          if (ws.path) commit('SET_PATH', ws.path)
          commit('SET_COMPLETED', ws.completed)
          commit('SET_VISTA_ORQUESTADA_SEEN', ws.vista_orquestada_seen)
          commit('SET_ORG_CREATED', true)
        } else if (rootState.auth.account && rootState.auth.account.id) {
          // Account exists but no onboarding record — create one
          commit('SET_ORG_CREATED', true)
          commit('SET_STATE_LOADED', true)
          return accountApi.create('onboardings', {})
            .then(res => {
              commit('SET_ONBOARDING_ID', res.data.id)
            })
            .catch(() => {})
        }
        commit('SET_STATE_LOADED', true)
      })
  },

  SAVE_STATE: ({ state }) => {
    if (!state.onboardingId) return Promise.resolve()
    const payload = {}
    ORG_FIELDS.forEach(key => {
      payload[key] = state.responses[key]
    })
    payload.path = state.path
    payload.completed = state.completed
    payload.vista_orquestada_seen = state.vistaOrquestadaSeen
    return accountApi.update('onboardings', state.onboardingId, payload)
  },

  SET_RESPONSE: ({ commit }, { key, value }) => {
    commit('SET_RESPONSE', { key, value })
  },
  COMPUTE_PATH: ({ commit, state }) => {
    const path = computePath(state.responses)
    commit('SET_PATH', path)
    return path
  },
  COMPLETE: ({ commit, dispatch }) => {
    dispatch('COMPUTE_PATH')
    commit('SET_COMPLETED', true)
    return Promise.all([
      dispatch('SAVE_USER_PROFILE'),
      dispatch('SAVE_STATE')
    ])
  },
  SEED_TEST_DATA: ({ state }) => {
    if (!state.onboardingId) return Promise.resolve()
    return accountApi.create('onboardings', {}, 'seed_test_data', state.onboardingId)
  },
  RESET: ({ commit }) => {
    commit('RESET')
  },
  RESET_FOR_NEW_ORG: ({ commit, state }) => {
    const role = state.responses.role
    const channels = [...state.responses.channels]
    commit('RESET')
    if (role) {
      commit('SET_RESPONSE', { key: 'role', value: role })
    }
    if (channels.length) {
      commit('SET_RESPONSE', { key: 'channels', value: channels })
    }
  }
}

const mutations = {
  SET_RESPONSE: (state, { key, value }) => {
    Vue.set(state.responses, key, value)
  },
  SET_PATH: (state, path) => {
    state.path = path
  },
  SET_COMPLETED: (state, value) => {
    state.completed = value
  },
  SET_ORG_CREATED: (state, value) => {
    state.orgCreated = value
  },
  SET_VISTA_ORQUESTADA_SEEN: (state, value) => {
    state.vistaOrquestadaSeen = value
  },
  SET_ONBOARDING_ID: (state, id) => {
    state.onboardingId = id
  },
  SET_PROFILE_LOADED: (state, value) => {
    state.profileLoaded = value
  },
  SET_STATE_LOADED: (state, value) => {
    state.stateLoaded = value
  },
  RESET: (state) => {
    state.responses = getDefaultResponses()
    state.path = ''
    state.completed = false
    state.orgCreated = false
    state.vistaOrquestadaSeen = false
    state.onboardingId = null
    state.stateLoaded = false
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations
}
